// Fitting things to the window without guessing at the chrome around them.
// docs/operations/design-standard.md, "Layout".

import { RefObject, useEffect } from 'react';

/**
 * The space the page keeps under `el`: its own bottom margin, then the bottom padding, border
 * and margin of every ancestor up to <body>. A box that ends this far above the window's bottom
 * leaves the page exactly as tall as the window, so nothing scrolls onto blank space.
 */
export function spaceBelow(el: HTMLElement): number {
  const num = (v: string) => parseFloat(v) || 0;
  let total = num(getComputedStyle(el).marginBottom);
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const cs = getComputedStyle(n);
    total += num(cs.paddingBottom) + num(cs.borderBottomWidth) + num(cs.marginBottom);
  }
  return Math.round(total);
}

export interface ViewportFillOptions {
  /** 'max' caps the box (short content stays short); 'height' makes it fill. */
  mode?: 'max' | 'height';
  /** Below this the box stops shrinking and the page scrolls instead of crushing it. */
  min?: number;
  enabled?: boolean;
}

/**
 * Sizes `ref` to the rest of the window: from its own top edge (document-relative, so the scroll
 * position never skews it) down to the window's bottom, less the space the page keeps below it.
 * Measures, never guesses: the nav, banners and filter rows above it can change height freely.
 * Re-measures on resize and whenever the body reflows. svh, because vh lies under mobile chrome.
 */
export function useViewportFill(ref: RefObject<HTMLElement | null>, { mode = 'max', min = 320, enabled = true }: ViewportFillOptions = {}): void {
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    const prop = mode === 'max' ? 'maxHeight' : 'height';
    const measure = () => {
      const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
      el.style[prop] = `max(${min}px, calc(100svh - ${top + spaceBelow(el)}px))`;
    };
    measure();
    window.addEventListener('resize', measure);
    // Converges rather than loops: re-setting an identical value does not change body layout,
    // so the observer goes quiet. Guarded for jsdom.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
      el.style[prop] = '';
    };
  }, [ref, mode, min, enabled]);
}
